import { useNavigationState } from '../navigation/NavigationProvider';
import { useBillingSummary } from '../billing/BillingSummaryProvider';
import { useAlertSource, useAlerts } from '../alerts/AlertsProvider';
import { AppAlert } from '../alerts/alertStore';
import { InventoryMutationFeedback } from '../items/InventoryMutationFeedback';
import { useLocationOperations } from '../items/useLocationOperations';
import { LocationOperationFeedback } from '../items/LocationOperationFeedback';
import { useCollectionBillingAccess } from '../capacity/useCollectionBillingAccess';
import {
  invalidateInventory,
  subscribeInventoryChanges,
} from './inventoryInvalidation';
import { useMoveLocations } from '../locations/useMoveLocations';
import { useCollectionItemImages } from '../images/useCollectionItemImages';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  CollectionSummary,
  InventoryAuthority,
  hasCollectionCapability,
} from '@qkindex/contracts';
import { Box, Typography } from '@mui/material';
import { Item } from '../../domain/item';
import { Location } from '../../domain/location';
import { AppTab, NavigationCount } from '../../components/ui/navigation';
import { MobilePrimaryAction } from '../../components/ui/MobileFooter';
import AddLocationModal from '../../components/AddLocationModal';
import { useUiPreferences } from '../../hooks/useUiPreferences';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { ItemsPage } from '../items/ItemsPage';
import { LocationsPage } from '../locations/LocationsPage';
import { useItemWorkflows } from '../items/useItemWorkflows';
import { ItemWorkflowModals } from '../items/ItemWorkflowModals';
import { useLocationEditorActions } from '../locations/useLocationEditorActions';
import { LocationEditorDialog } from '../locations/components/LocationEditorDialog';
import {
  CaptureEnqueue,
  MultiCollectionRapidCapture,
} from '../rapidCapture/MultiCollectionRapidCapture';
import { locationService } from '../../services/locationService';
import { useMultiCollectionInventory } from './useMultiCollectionInventory';
import { collectionCapabilities } from './collectionCapabilities';
import { recordKey, scopeRecord } from './multiCollectionIdentity';
import { RegisterCaptureQueue } from '../rapidCapture/CaptureQueueBadge';
import { mergeCaptureInventory } from '../rapidCapture/captureInventory';
import { ItemEditEnqueue } from '../rapidCapture/rapidCaptureTypes';

export function MultiCollectionWorkspace({
  userId,
  username,
  collections,
  collectionIds,
  activeTab,
  phoneLayout,
  shellHeader,
  ui,
  discovering = false,
  discoveryError = false,
  onRetryDiscovery,
  onViewCollection,
  onOpenBilling,
  onManageCollection,
  onManageTags,
  renderMobileFooter,
  registerCaptureQueue,
}: {
  userId: string;
  username?: string;
  collections: CollectionSummary[];
  collectionIds: string[];
  activeTab: AppTab;
  phoneLayout: boolean;
  shellHeader: ReactNode;
  ui: ReturnType<typeof useUiPreferences>;
  renderMobileFooter?: (
    action?: MobilePrimaryAction,
    counts?: { items?: NavigationCount; locations?: NavigationCount },
  ) => ReactNode;
  discovering?: boolean;
  discoveryError?: boolean;
  onRetryDiscovery: () => void;
  registerCaptureQueue?: RegisterCaptureQueue;
  onOpenBilling?: () => void;
  onManageCollection?: (collectionId: string) => void;
  onManageTags?: (collectionId: string) => void;
  onViewCollection?: (collectionId: string) => void;
}) {
  const [itemCount, setItemCount] = useState<NavigationCount>();
  const [locationCount, setLocationCount] = useState<NavigationCount>();
  const { preferences } = ui;
  const alerts = useAlerts();
  const { snapshot: accountBilling } = useBillingSummary(userId);
  const [loadLocations, setLoadLocations] = useState(false);
  const [detailCollectionId, setDetailCollectionId] = useState<string>();
  const captureQueues = useRef(new Map<string, CaptureEnqueue>());
  const editQueues = useRef(new Map<string, ItemEditEnqueue>());
  const [captureItems, setCaptureItems] = useState(new Map<string, Item[]>());
  const registerDraftItems = useCallback(
    (collectionId: string, items: Item[]) => {
      const key = JSON.stringify([userId, collectionId]);
      setCaptureItems((previous) => {
        // UI callbacks are stable per capture identity; compare their display values.
        if (JSON.stringify(previous.get(key) || []) === JSON.stringify(items))
          return previous;
        const next = new Map(previous);
        if (items.length) next.set(key, items);
        else next.delete(key);
        return next;
      });
    },
    [userId],
  );
  const registerEnqueue = useCallback(
    (collectionId: string, enqueue: CaptureEnqueue | undefined) => {
      if (enqueue) captureQueues.current.set(collectionId, enqueue);
      else captureQueues.current.delete(collectionId);
    },
    [],
  );
  const registerEdit = useCallback(
    (collectionId: string, save: ItemEditEnqueue | undefined) => {
      if (save) editQueues.current.set(collectionId, save);
      else editQueues.current.delete(collectionId);
    },
    [],
  );
  const [addLocationOpen, setAddLocationOpen] = useNavigationState(
    'workspace.location.add',
    false,
  );
  const [destination, setDestination] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [description, setDescription] = useState('');
  const [locationError, setLocationError] = useState<string | null>(null);
  const [savingLocation, setSavingLocation] = useState(false);
  const [extraLocations, setExtraLocations] = useState<Location[]>([]);
  const [editingLocationKey, setEditingLocationKey] = useNavigationState<
    string | null
  >('workspace.location.edit', null);
  const setEditingLocation = useCallback(
    (location: Location | null) =>
      setEditingLocationKey(location ? recordKey(location) : null),
    [setEditingLocationKey],
  );
  const [lastLocation, setLastLocation] = useState(() => {
    try {
      return (
        localStorage.getItem(`qkindex.lastUsedLocation.${userId}`) ||
        localStorage.getItem(`qkindex.lastBrowseLocation.${userId}`) ||
        ''
      );
    } catch {
      return '';
    }
  });
  const inventory = useMultiCollectionInventory({
    userId,
    collections,
    collectionIds:
      detailCollectionId && !collectionIds.includes(detailCollectionId)
        ? [...collectionIds, detailCollectionId]
        : collectionIds,
    itemSort: preferences.itemSort,
  });
  const currentCollections = inventory.collections;
  const allCollections = collections
    .map(
      (entry) =>
        currentCollections.find(
          (current) => current.collectionId === entry.collectionId,
        ) || entry,
    )
    .filter(
      (entry) =>
        !inventory.unavailableCollectionIds.includes(entry.collectionId),
    );
  const billingRestricted = useCollectionBillingAccess(userId, allCollections);
  const visibleCollections = currentCollections.filter((entry) =>
    collectionIds.includes(entry.collectionId),
  );
  const moveDestinations = useMoveLocations(
    userId,
    visibleCollections,
    loadLocations,
  );
  const visibleMoveLocations = moveDestinations.locations
    .filter((entry) => collectionIds.includes(entry.collectionId || ''))
    .map((entry) => ({
      ...entry,
      collectionName:
        collectionIds.length > 1 ? entry.collectionName : undefined,
    }));
  // Optimistic records only bridge the gap until a reader observes the write.
  const observedLocationKeys = JSON.stringify(
    [...inventory.locations, ...moveDestinations.locations].map(recordKey),
  );
  useEffect(() => {
    const observed = new Set<string>(JSON.parse(observedLocationKeys));
    setExtraLocations((previous) => {
      const remaining = previous.filter(
        (location) => !observed.has(recordKey(location)),
      );
      return remaining.length === previous.length ? previous : remaining;
    });
  }, [observedLocationKeys]);
  useEffect(
    () =>
      subscribeInventoryChanges(userId, (collectionId, deletedLocationIds) => {
        if (!deletedLocationIds?.length) return;
        setExtraLocations((previous) =>
          previous.filter(
            (location) =>
              location.collectionId !== collectionId ||
              !deletedLocationIds.includes(location.id),
          ),
        );
      }),
    [userId],
  );
  const inventoryItems = useCollectionItemImages(
    userId,
    inventory.items,
    currentCollections,
    inventory.itemsRevision ?? 0,
  );
  const writable = currentCollections.filter(
    (entry) =>
      collectionIds.includes(entry.collectionId) &&
      entry.lifecycle === 'active' &&
      entry.role !== 'viewer' &&
      !billingRestricted.has(entry.collectionId),
  );
  const locationDestinations = allCollections.filter(
    (entry) =>
      collectionIds.includes(entry.collectionId) &&
      entry.lifecycle === 'active' &&
      !billingRestricted.has(entry.collectionId) &&
      hasCollectionCapability(entry.role, 'editInventory'),
  );
  const scopedLocations = [
    ...new Map(
      [...extraLocations, ...moveDestinations.locations, ...inventory.locations]
        .filter(
          (entry) =>
            collectionIds.includes(entry.collectionId || '') &&
            currentCollections.some(
              (collection) => collection.collectionId === entry.collectionId,
            ),
        )
        .map((entry) => [recordKey(entry), entry]),
    ).values(),
  ].map((entry) => ({
    ...entry,
    collectionName: collectionIds.length > 1 ? entry.collectionName : undefined,
  }));
  const editingLocation =
    scopedLocations.find((entry) => recordKey(entry) === editingLocationKey) ||
    null;
  const single =
    collectionIds.length === 1
      ? currentCollections.find(
          (entry) => entry.collectionId === collectionIds[0],
        )
      : undefined;
  const authorityFor = (
    collectionId?: string,
  ): InventoryAuthority | undefined => {
    const summary = currentCollections.find(
      (entry) => entry.collectionId === collectionId,
    );
    return summary &&
      !billingRestricted.has(summary.collectionId) &&
      collectionCapabilities('collection-only', summary.role, summary.lifecycle)
        .canEditInventory
      ? {
          userId,
          collectionId: summary.collectionId,
          compatibilityMode: 'collection-only',
        }
      : undefined;
  };
  const updateLastLocation = async (value: string) => {
    setLastLocation(value);
    try {
      localStorage.setItem(`qkindex.lastUsedLocation.${userId}`, value);
      localStorage.setItem(`qkindex.lastBrowseLocation.${userId}`, value);
    } catch {
      /* Optional preference. */
    }
  };
  const openAddLocation = (collectionId?: string) => {
    setLoadLocations(true);
    setDestination(
      locationDestinations.some((entry) => entry.collectionId === collectionId)
        ? collectionId!
        : locationDestinations.length === 1
          ? locationDestinations[0].collectionId
          : locationDestinations.some(
                (entry) => entry.collectionId === destination,
              )
            ? destination
            : '',
    );
    setLocationError(null);
    setAddLocationOpen(true);
  };
  const locationOperations = useLocationOperations({
    userId,
    collections: allCollections,
    restricted: billingRestricted,
    locations: scopedLocations,
    onViewCollection,
  });
  const writableCollections = allCollections.filter(
    (entry) =>
      entry.lifecycle === 'active' &&
      entry.role !== 'viewer' &&
      !billingRestricted.has(entry.collectionId),
  );
  const enqueuePhoto: CaptureEnqueue = async (draft) => {
    const destination = scopedLocations.find(
      (entry) => recordKey(entry) === draft.locationId,
    );
    const enqueue = destination?.collectionId
      ? captureQueues.current.get(destination.collectionId)
      : undefined;
    if (!enqueue)
      throw new Error(
        'Capture access is unavailable. Choose a writable location or try again.',
      );
    return enqueue(draft);
  };
  const visibleDraftItems = currentCollections.flatMap((collection) =>
    collectionIds.includes(collection.collectionId) &&
    hasCollectionCapability(collection.role, 'editInventory') &&
    collection.lifecycle === 'active'
      ? captureItems.get(JSON.stringify([userId, collection.collectionId])) ||
        []
      : [],
  );
  const presentationItems = mergeCaptureInventory(
    inventoryItems,
    visibleDraftItems,
  )
    .filter((entry) => collectionIds.includes(entry.collectionId || ''))
    .map((entry) => ({
      ...entry,
      collectionName:
        collectionIds.length > 1 ? entry.collectionName : undefined,
    }));
  const workflow = useItemWorkflows({
    enqueuePhoto,
    enqueueEdit: async (item, fields, image, options) => {
      const save = item.collectionId
        ? editQueues.current.get(item.collectionId)
        : undefined;
      if (!save)
        throw new Error(
          'Item editing access is unavailable. Reopen the item and try again.',
        );
      return save(item, fields, image, options);
    },
    addCollectionId: single?.collectionId,
    showTagCollectionName: collectionIds.length > 1,
    onManageTags,
    collectionContext: { userId, collections: allCollections },
    currentUsername: username,
    billingRestricted,
    items: presentationItems,
    locations: scopedLocations,
    moveLocations: visibleMoveLocations,
    moveLocationsLoading: moveDestinations.loading,
    moveLocationsNotice: moveDestinations.failed
      ? 'Some destination locations could not be loaded. Reopen this item to retry.'
      : undefined,
    onMoveItem: async (item, location, tagIds) =>
      (
        await locationOperations.start([item], location, tagIds)
      ).completed.includes(recordKey(item)),
    lastUsedLocation: scopedLocations.some(
      (entry) =>
        recordKey(entry) === lastLocation &&
        writableCollections.some(
          (collection) => collection.collectionId === entry.collectionId,
        ),
    )
      ? lastLocation
      : '',
    updateLastUsedLocation: updateLastLocation,
    onAddLocation: openAddLocation,
  });
  const failedIds = inventory.failedCollectionIds;
  const unavailableIds = inventory.unavailableCollectionIds;
  useEffect(() => {
    const rejected = [...failedIds, ...unavailableIds];
    const selectedCollectionId =
      workflow.modalProps.itemDetails.selectedItem?.collectionId;
    if (selectedCollectionId && rejected.includes(selectedCollectionId))
      workflow.resetForCollectionChange();
    if (
      editingLocation?.collectionId &&
      rejected.includes(editingLocation.collectionId)
    )
      setEditingLocation(null);
  }, [
    workflow,
    failedIds,
    unavailableIds,
    editingLocation,
    setEditingLocation,
  ]);
  const locationSummary = currentCollections.find(
    (entry) => entry.collectionId === editingLocation?.collectionId,
  );
  const canMoveFrom = (collectionId?: string) =>
    currentCollections.some(
      (entry) =>
        entry.collectionId === collectionId &&
        entry.lifecycle === 'active' &&
        !billingRestricted.has(entry.collectionId) &&
        hasCollectionCapability(entry.role, 'moveItemsOut'),
    );
  const addLocationDestinations = locationDestinations.filter(
    (entry) =>
      !workflow.modalProps.itemDetails.open ||
      canMoveFrom(workflow.modalProps.itemDetails.selectedItem?.collectionId) ||
      entry.collectionId ===
        workflow.modalProps.itemDetails.selectedItem?.collectionId,
  );
  const locationDestination =
    addLocationDestinations.length === 1
      ? addLocationDestinations[0].collectionId
      : addLocationDestinations.some(
            (entry) => entry.collectionId === destination,
          )
        ? destination
        : '';
  const locationEditor = useLocationEditorActions(
    authorityFor(editingLocation?.collectionId),
  );
  const { requestClose: closeAddLocation, closeAfterSave: finishAddLocation } =
    useUnsavedChangesGuard({
      scopeKey: 'workspace.location.add',
      open: addLocationOpen,
      dirty: Boolean(newLocation || description),
      pending: savingLocation,
      onDiscard: () => {
        setNewLocation('');
        setDescription('');
      },
      onClose: () => setAddLocationOpen(false),
    });
  const addLocation = async () => {
    if (savingLocation) return;
    const authority = authorityFor(locationDestination);
    const summary = addLocationDestinations.find(
      (entry) => entry.collectionId === locationDestination,
    );
    if (!authority || !summary) {
      setLocationError('Choose a collection you can add locations to.');
      return;
    }
    setSavingLocation(true);
    try {
      const id = await locationService.create(
        authority,
        newLocation,
        description,
      );
      const created = scopeRecord(
        {
          id,
          name: newLocation.trim(),
          normalizedName: newLocation.trim().toLocaleLowerCase(),
          description: description.trim(),
          createdAt: new Date().toISOString(),
        },
        summary,
      );
      setExtraLocations((previous) => [...previous, created]);
      invalidateInventory({
        ...authority,
        resources: ['locations'],
        reloadLocationIds: [id],
      });
      workflow.setCurrentLocation(recordKey(created));
      await updateLastLocation(recordKey(created));
      finishAddLocation();
      setNewLocation('');
      setDescription('');
    } catch (error) {
      setLocationError(
        error instanceof Error
          ? error.message
          : 'Location could not be created.',
      );
    } finally {
      setSavingLocation(false);
    }
  };
  const openItem = (item: Item) => {
    setLoadLocations(true);
    if (moveDestinations.failed) moveDestinations.refresh();
    setDetailCollectionId(item.collectionId);
    workflow.openItem(item);
  };
  const openAdd = () => {
    setLoadLocations(true);
    workflow.openAdd();
  };
  const scopedAuthority: InventoryAuthority = {
    userId,
    collectionId: single?.collectionId || '',
    compatibilityMode: 'collection-only',
  };
  const singleEditor = useLocationEditorActions(
    single ? authorityFor(single.collectionId) : undefined,
  );
  const singleCanEdit = Boolean(single && authorityFor(single.collectionId));
  const inventoryStatus =
    inventory.offline || inventory.error
      ? undefined
      : inventory.loading || inventory.hasMore
        ? ('loading' as const)
        : inventory.refreshing
          ? ('updating' as const)
          : undefined;
  const common = {
    inventoryStatus,
    onRefresh: inventory.refresh,
    phoneLayout,
    stickyTop: 0,
    cardSize: preferences.cardSize,
    removeMargins: preferences.removeMargins,
  };
  const workspaceAlerts: AppAlert[] = [];
  if (discoveryError)
    workspaceAlerts.push({
      id: 'discovery',
      title: 'Some collections could not be discovered',
      message: 'Results are incomplete.',
      severity: 'warning',
      action: { label: 'Retry', run: onRetryDiscovery },
    });
  if (inventory.error)
    workspaceAlerts.push({
      id: 'inventory',
      title: 'Inventory could not be fully loaded',
      message: inventory.error,
      severity: 'warning',
      action: { label: 'Retry', run: inventory.retry },
    });
  if (inventory.offline)
    workspaceAlerts.push({
      id: 'offline',
      title: 'Offline',
      message: 'Showing saved inventory. Updates will resume when connected.',
      severity: 'info',
    });
  for (const collection of allCollections) {
    if (
      billingRestricted.has(collection.collectionId) &&
      !(
        collection.role === 'owner' &&
        accountBilling.billing?.itemBilling.restricted
      )
    )
      workspaceAlerts.push({
        id: `restricted:${collection.collectionId}`,
        title: `${collection.name}: adding and editing paused`,
        severity: 'warning',
        message:
          'The collection is currently restricted. Viewing, exporting and deletion remain available under your role permissions. The owner can check Billing for credit restrictions.',
        action:
          collection.role === 'owner' && onOpenBilling
            ? { label: 'Open billing', run: onOpenBilling, navigate: true }
            : undefined,
      });
    if (collection.activeTransferId && collection.role !== 'viewer')
      workspaceAlerts.push({
        id: `transfer:${collection.collectionId}`,
        title: `${collection.name}: ownership offer pending`,
        severity: 'info',
        message:
          'The collection stays active. Open collection settings to view the ownership offer.',
        action: onManageCollection
          ? {
              label: 'View offer',
              run: () => onManageCollection(collection.collectionId),
              navigate: true,
            }
          : undefined,
      });
  }
  useAlertSource(userId, 'workspace', workspaceAlerts);
  const unavailableKey = JSON.stringify(
    [...inventory.unavailableCollectionIds].sort(),
  );
  useEffect(() => {
    const ids = JSON.parse(unavailableKey) as string[];
    if (ids.length)
      alerts?.notice(userId, {
        id: `unavailable:${unavailableKey}`,
        title: 'Collections no longer available',
        severity: 'info',
        message: `${ids.length} selected collection${ids.length === 1 ? ' is' : 's are'} no longer available. Their entries have been removed.`,
      });
  }, [alerts, userId, unavailableKey]);
  return (
    <>
      <LocationOperationFeedback
        userId={userId}
        controller={locationOperations}
        collections={allCollections}
      />
      <InventoryMutationFeedback userId={userId} collections={allCollections} />
      {(discovering ||
        discoveryError ||
        inventory.error ||
        inventory.offline ||
        billingRestricted.size > 0) && (
        <Typography
          role="status"
          variant="caption"
          sx={{ display: 'block', px: 2 }}
        >
          {[
            discovering && 'Finding collections…',
            (discoveryError || inventory.error) &&
              'Results incomplete — see Alerts',
            inventory.offline && 'Offline — saved inventory',
            billingRestricted.size > 0 &&
              'Some collections restrict editing — see Alerts',
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      )}
      <Box sx={{ display: activeTab === 'items' ? 'block' : 'none' }}>
        <ItemsPage
          {...common}
          hideCount={phoneLayout && Boolean(renderMobileFooter)}
          onCountChange={renderMobileFooter ? setItemCount : undefined}
          shellHeader={activeTab === 'items' ? shellHeader : undefined}
          authority={scopedAuthority}
          items={presentationItems}
          locations={scopedLocations}
          loading={inventory.loading && !presentationItems.length}
          error={null}
          readOnly={!writable.length}
          tagCollections={currentCollections.filter((collection) =>
            collectionIds.includes(collection.collectionId),
          )}
          locationFilterScope={{
            collectionIds,
            pending: discovering || discoveryError,
            unavailableCollectionIds: inventory.unavailableCollectionIds,
            completeCollectionIds:
              inventory.completeLocationCollectionIds || [],
          }}
          canDelete={Boolean(
            single &&
            collectionCapabilities(
              'collection-only',
              single.role,
              single.lifecycle,
            ).canEditInventory,
          )}
          disableBulkActions={!singleCanEdit}
          incompleteResults={inventory.incomplete || discovering}
          view={preferences.itemView}
          onViewChange={ui.setItemView}
          sort={preferences.itemSort}
          onSortChange={ui.setItemSort}
          onOpenItem={openItem}
          onMoveSelection={
            writable.length ? locationOperations.start : undefined
          }
          locationMoveError={locationOperations.error}
          moveLocations={scopedLocations.filter((entry) =>
            locationDestinations.some(
              (collection) => collection.collectionId === entry.collectionId,
            ),
          )}
          moveLocationsLoading={moveDestinations.loading}
          onLoadMoveLocations={() => {
            setLoadLocations(true);
            if (moveDestinations.failed) moveDestinations.refresh();
          }}
          onAddItem={openAdd}
          updateLastUsedLocation={updateLastLocation}
        />
      </Box>
      <Box sx={{ display: activeTab === 'locations' ? 'block' : 'none' }}>
        <LocationsPage
          {...common}
          hideCount={phoneLayout && Boolean(renderMobileFooter)}
          onCountChange={renderMobileFooter ? setLocationCount : undefined}
          shellHeader={activeTab === 'locations' ? shellHeader : undefined}
          controller={{
            items: inventoryItems.filter((entry) =>
              collectionIds.includes(entry.collectionId || ''),
            ),
            locations: scopedLocations,
            update: async (updates) => {
              if (!singleCanEdit) return;
              try {
                const failed = [];
                // Each rename holds a collection-wide mutation lock until it settles.
                for (const update of updates) {
                  const location = scopedLocations.find(
                    (entry) => entry.id === update.id,
                  );
                  try {
                    if (location)
                      await locationService.rename(
                        scopedAuthority,
                        update.id,
                        update.name ?? location.name,
                        update.description ?? location.description,
                      );
                  } catch {
                    failed.push(update);
                  }
                }
                invalidateInventory({
                  ...scopedAuthority,
                  resources: ['items', 'locations'],
                });
                if (failed.length)
                  alerts?.notice(userId, {
                    id: `location-update:${scopedAuthority.collectionId}`,
                    title: 'Some locations could not be updated',
                    message: `${failed.length} updates failed. Other updates have finished. Refresh to check their current state.`,
                    severity: 'warning',
                  });
              } catch {
                alerts?.notice(userId, {
                  id: `location-update:${scopedAuthority.collectionId}`,
                  title: 'Locations could not be updated',
                  message: 'Check their current state before trying again.',
                  severity: 'error',
                });
              }
            },
            delete: async (ids) => {
              if (!singleCanEdit) return;
              try {
                const selected = scopedLocations.filter((entry) =>
                  ids.includes(entry.id),
                );
                const result = await locationService.deleteMany(
                  scopedAuthority,
                  selected,
                );
                invalidateInventory({
                  ...scopedAuthority,
                  resources: ['locations'],
                  deletedLocationIds: result.deletedLocationIds,
                });
                if (result.failedLocationIds.length)
                  alerts?.notice(userId, {
                    id: `location-delete:${scopedAuthority.collectionId}`,
                    title: 'Some locations could not be deleted',
                    message: `${result.deletedLocationIds.length} deleted, ${result.failedLocationIds.length} failed. Refresh to check their current state.`,
                    severity: 'warning',
                  });
              } catch {
                alerts?.notice(userId, {
                  id: `location-delete:${scopedAuthority.collectionId}`,
                  title: 'Locations could not be deleted',
                  message: 'Check their current state before trying again.',
                  severity: 'error',
                });
              }
            },
            ...singleEditor,
          }}
          loading={inventory.loading && !inventory.locations.length}
          incompleteResults={inventory.incomplete || discovering}
          itemCounts={inventory.incomplete ? {} : undefined}
          onOpenLocation={(entry) => setEditingLocation(entry)}
          onAddLocation={() => openAddLocation()}
          readOnly={!writable.length}
          disableBulkActions={!singleCanEdit}
          view={preferences.locationView}
          onViewChange={ui.setLocationView}
          sort={preferences.locationSort}
          onSortChange={ui.setLocationSort}
          currentUserId={userId}
          currentUsername={username}
        />
      </Box>
      {phoneLayout &&
        renderMobileFooter?.(
          writable.length
            ? {
                label: activeTab === 'items' ? 'Add item' : 'Add location',
                onClick:
                  activeTab === 'items' ? openAdd : () => openAddLocation(),
              }
            : undefined,
          { items: itemCount, locations: locationCount },
        )}
      <ItemWorkflowModals workflow={workflow} enqueue={enqueuePhoto} />
      <LocationEditorDialog
        scopeKey="workspace.location.edit"
        open={Boolean(editingLocation)}
        location={editingLocation}
        onAddItem={
          editingLocation && locationSummary?.lifecycle === 'active'
            ? () => {
                const location = editingLocation;
                setLoadLocations(true);
                void workflow.openAddAtLocation(
                  location,
                  'workspace.location.edit',
                );
              }
            : undefined
        }
        onClose={() => setEditingLocation(null)}
        onSave={locationEditor.saveLocation}
        onDelete={locationEditor.deleteLocation}
        readOnly={!authorityFor(editingLocation?.collectionId)}
        currentUserId={userId}
        currentUsername={username}
        collectionId={editingLocation?.collectionId}
        membershipVersion={locationSummary?.membershipVersion}
      />
      <AddLocationModal
        open={addLocationOpen}
        onClose={() => {
          void closeAddLocation();
        }}
        newLocation={newLocation}
        setNewLocation={setNewLocation}
        newLocationDescription={description}
        setNewLocationDescription={setDescription}
        locationError={locationError}
        handleAddLocation={addLocation}
        collectionOptions={addLocationDestinations.map(
          ({ collectionId, name }) => ({ collectionId, name }),
        )}
        collectionId={locationDestination}
        onCollectionChange={(collectionId) => {
          setDestination(collectionId);
          setDetailCollectionId(collectionId);
        }}
      />
      <MultiCollectionRapidCapture
        userId={userId}
        collections={collections}
        locations={scopedLocations}
        items={inventoryItems}
        captureCollectionId={
          workflow.modalProps.addItem.open
            ? scopedLocations.find(
                (location) =>
                  recordKey(location) === workflow.modalProps.addItem.location,
              )?.collectionId
            : undefined
        }
        registerEnqueue={registerEnqueue}
        registerEdit={registerEdit}
        registerCaptureQueue={registerCaptureQueue}
        onDraftItemsChange={registerDraftItems}
        onOpenItem={openItem}
      />
    </>
  );
}
